import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useUser } from '../context';
import type { SchoolPayload } from './checkInTypes';
import { ACCOUNT_BILLING_PATH } from './accountPaths';

type FieldErrors = {
  schoolId?: string;
  name?: string;
  phoneNumber?: string;
  address?: string;
};

type StudentLimitStatus = {
  studentCount: number;
  limit: number | null;
  planName: string | null;
  overLimit: boolean;
};

type SchoolRole = 'owner' | 'staff' | 'none';

type SchoolAccessPayload = {
  id: string;
  schoolId: string;
  schoolName: string;
  status: 'pending' | 'approved' | 'rejected' | 'revoked';
  note: string;
  createdAt: string;
  decidedAt: string | null;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
};

type OptionKind = 'grades' | 'rooms' | 'teachers';

const KIND_LABEL: Record<OptionKind, string> = {
  grades: 'Grade',
  rooms: 'Room',
  teachers: 'Teacher',
};

const SCHOOL_ID_PATTERN = /^[A-Za-z][A-Za-z0-9-]*$/;
const SCHOOL_ID_FORMAT_HINT =
  'Must start with a letter; letters, numbers, and hyphens only; no spaces; under 128 characters.';

/** Client-side School ID rules (aligned with server `normalizeSchoolId`). */
const validateSchoolId = (raw: string): string | undefined => {
  const schoolId = raw.trim();
  if (!schoolId) {
    return 'School ID is required.';
  }
  if (schoolId.length >= 128 || /\s/.test(schoolId) || !SCHOOL_ID_PATTERN.test(schoolId)) {
    return SCHOOL_ID_FORMAT_HINT;
  }
  return undefined;
};

const FALLBACK_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Kolkata',
  'Australia/Sydney',
];

const getBrowserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const getTimeZoneOptions = (current?: string): string[] => {
  let zones: string[];
  try {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf?.('timeZone');
    zones = supported && supported.length > 0 ? [...supported] : [...FALLBACK_TIMEZONES];
  } catch {
    zones = [...FALLBACK_TIMEZONES];
  }
  if (!zones.includes('UTC')) zones.unshift('UTC');
  if (current && !zones.includes(current)) zones.push(current);
  return zones;
};

const inputClassName =
  'mt-1 block w-full rounded-xl border-2 border-ink/10 bg-white px-3 py-2 font-sans text-base font-semibold text-ink shadow-sm focus:border-skysoft focus:outline-none focus:ring-2 focus:ring-skysoft/60';

const inputErrorClassName =
  'mt-1 block w-full rounded-xl border-2 border-doghouse/50 bg-white px-3 py-2 font-sans text-base font-semibold text-ink shadow-sm focus:border-doghouse focus:outline-none focus:ring-2 focus:ring-doghouse/40';

const validateSchoolForm = (values: {
  schoolId: string;
  name: string;
  phoneNumber: string;
  address: string;
}): FieldErrors => {
  const errors: FieldErrors = {};
  const name = values.name.trim();
  const phoneNumber = values.phoneNumber.trim();
  const address = values.address.trim();

  const schoolIdError = validateSchoolId(values.schoolId);
  if (schoolIdError) {
    errors.schoolId = schoolIdError;
  }

  if (!name) {
    errors.name = 'School name is required.';
  } else if (name.length > 200) {
    errors.name = 'School name must be 200 characters or fewer.';
  }

  if (!phoneNumber) {
    errors.phoneNumber = 'Phone number is required.';
  } else if (phoneNumber.length > 40) {
    errors.phoneNumber = 'Phone number must be 40 characters or fewer.';
  } else if (!/^[\d+\-().\s]+$/.test(phoneNumber)) {
    errors.phoneNumber = 'Enter a valid phone number.';
  }

  if (!address) {
    errors.address = 'Address is required.';
  } else if (address.length > 500) {
    errors.address = 'Address must be 500 characters or fewer.';
  }

  return errors;
};

const OptionTableEditor = ({
  kind,
  items,
  onSchoolUpdated,
}: {
  kind: OptionKind;
  items: string[];
  onSchoolUpdated: (school: SchoolPayload) => void;
}) => {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    [items],
  );

  const handleAdd = useCallback(async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError(`${KIND_LABEL[kind]} is required.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/school/checkin-settings/${kind}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: trimmed }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? `Add failed (${res.status})`);
      }
      const data = (await res.json()) as { school: SchoolPayload };
      onSchoolUpdated(data.school);
      setValue('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Add failed');
    } finally {
      setSaving(false);
    }
  }, [kind, onSchoolUpdated, value]);

  const handleRemove = useCallback(
    async (item: string) => {
      setSaving(true);
      setError(null);
      try {
        const res = await fetch(`/api/school/checkin-settings/${kind}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: item }),
        });
        if (!res.ok) {
          const data = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(data?.error ?? `Remove failed (${res.status})`);
        }
        const data = (await res.json()) as { school: SchoolPayload };
        onSchoolUpdated(data.school);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Remove failed');
      } finally {
        setSaving(false);
      }
    },
    [kind, onSchoolUpdated],
  );

  return (
    <div className="rounded-2xl border-2 border-ink/10 bg-paper/40 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <h3 className="font-display text-base font-semibold text-ink">{KIND_LABEL[kind]}s</h3>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <input
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleAdd();
              }
            }}
            placeholder={`Add ${KIND_LABEL[kind].toLowerCase()}`}
            className="block w-full rounded-xl border-2 border-ink/10 bg-white px-3 py-2 font-sans text-sm font-semibold text-ink shadow-sm focus:border-skysoft focus:outline-none focus:ring-2 focus:ring-skysoft/60 sm:w-56"
          />
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleAdd()}
            className="shrink-0 rounded-full bg-skysoft/70 px-4 py-2 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-skysoft disabled:opacity-50"
          >
            Add
          </button>
        </div>
      </div>

      {error ? (
        <p className="mt-2 text-xs font-semibold text-doghouse" role="alert">
          {error}
        </p>
      ) : null}

      <div className="mt-4 overflow-x-auto rounded-xl border-2 border-ink/10 bg-white">
        <table className="min-w-full divide-y divide-ink/10 text-left">
          <thead className="bg-paper/70">
            <tr className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
              <th className="px-4 py-3">{KIND_LABEL[kind]}</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/10">
            {sortedItems.length === 0 ? (
              <tr>
                <td colSpan={2} className="px-4 py-6 text-center font-sans text-sm text-ink/55">
                  No {KIND_LABEL[kind].toLowerCase()}s yet.
                </td>
              </tr>
            ) : (
              sortedItems.map((item) => (
                <tr key={item} className="font-sans text-sm text-ink">
                  <td className="px-4 py-3 font-semibold">{item}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void handleRemove(item)}
                      className="rounded-full px-3 py-1.5 text-sm font-semibold text-doghouse ring-1 ring-doghouse/30 hover:bg-doghouse/10 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-2 font-sans text-xs text-ink/55">
        {sortedItems.length === 1
          ? `1 ${KIND_LABEL[kind].toLowerCase()} total`
          : `${sortedItems.length} ${KIND_LABEL[kind].toLowerCase()}s total`}
      </p>
    </div>
  );
};

const formatAccessUserName = (user?: SchoolAccessPayload['user']) => {
  if (!user) return 'Unknown user';
  const name = `${user.firstName} ${user.lastName}`.trim();
  return name || user.email;
};

const randomFourDigitCode = () =>
  String(Math.floor(1000 + Math.random() * 9000));

const JoinSchoolForm = ({
  onSubmitted,
}: {
  onSubmitted: (access: SchoolAccessPayload) => void;
}) => {
  const [joinSchoolId, setJoinSchoolId] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const schoolId = joinSchoolId.trim();
      if (!schoolId) {
        setError('School ID is required.');
        return;
      }
      if (!/^\d{4}$/.test(confirmationCode.trim())) {
        setError('Confirmation code must be a 4-digit number.');
        return;
      }
      if (note.trim().length > 500) {
        setError('Note must be 500 characters or fewer.');
        return;
      }

      setSaving(true);
      setError(null);
      try {
        const res = await fetch('/api/school/access-requests', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            schoolId,
            note: note.trim(),
            accessConfirmationCode: confirmationCode.trim(),
          }),
        });
        if (!res.ok) {
          const data = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(data?.error ?? `Request failed (${res.status})`);
        }
        const data = (await res.json()) as { access: SchoolAccessPayload };
        onSubmitted(data.access);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Request failed');
      } finally {
        setSaving(false);
      }
    },
    [confirmationCode, joinSchoolId, note, onSubmitted],
  );

  return (
    <form className="mt-4 space-y-4" onSubmit={(event) => void handleSubmit(event)} noValidate>
      <label className="block">
        <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
          School ID
        </span>
        <input
          type="text"
          value={joinSchoolId}
          onChange={(event) => {
            setJoinSchoolId(event.target.value);
            setError(null);
          }}
          autoComplete="off"
          spellCheck={false}
          className={inputClassName}
          placeholder="e.g. LincolnElementary"
        />
      </label>
      <label className="block">
        <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
          Confirmation code
        </span>
        <input
          type="text"
          inputMode="numeric"
          pattern="\d{4}"
          maxLength={4}
          value={confirmationCode}
          onChange={(event) => {
            setConfirmationCode(event.target.value.replace(/\D/g, '').slice(0, 4));
            setError(null);
          }}
          autoComplete="off"
          spellCheck={false}
          className={`${inputClassName} tracking-[0.2em]`}
          placeholder="1234"
        />
        <p className="mt-1 font-sans text-xs text-ink/55">
          Ask the school owner for the 4-digit staff access confirmation code.
        </p>
      </label>
      <label className="block">
        <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
          Note for the school owner (optional)
        </span>
        <textarea
          value={note}
          rows={3}
          maxLength={500}
          onChange={(event) => {
            setNote(event.target.value);
            setError(null);
          }}
          className={inputClassName}
          placeholder="Who you are and why you need access"
        />
        <p className="mt-1 font-sans text-xs text-ink/55">{note.trim().length}/500</p>
      </label>
      {error ? (
        <p className="rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink" role="alert">
          {error}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={saving}
        className="rounded-full bg-skysoft/70 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-skysoft disabled:cursor-not-allowed disabled:opacity-50"
      >
        {saving ? 'Sending…' : 'Request access'}
      </button>
    </form>
  );
};

const AccessConfirmationCodeManager = ({
  school,
  onSchoolUpdated,
}: {
  school: SchoolPayload;
  onSchoolUpdated: (school: SchoolPayload) => void;
}) => {
  const [code, setCode] = useState(school.accessConfirmationCode ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setCode(school.accessConfirmationCode ?? '');
  }, [school.accessConfirmationCode]);

  const saveCode = useCallback(
    async (nextCode: string) => {
      setSaving(true);
      setError(null);
      setSaved(false);
      try {
        const res = await fetch('/api/school/access-confirmation-code', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accessConfirmationCode: nextCode }),
        });
        if (!res.ok) {
          const data = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(data?.error ?? `Save failed (${res.status})`);
        }
        const data = (await res.json()) as { school: SchoolPayload };
        onSchoolUpdated(data.school);
        setCode(data.school.accessConfirmationCode ?? nextCode);
        setSaved(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Save failed');
      } finally {
        setSaving(false);
      }
    },
    [onSchoolUpdated],
  );

  return (
    <div className="rounded-2xl border-2 border-ink/10 bg-paper/40 p-5">
      <h3 className="font-display text-base font-semibold text-ink">
        Staff confirmation code
      </h3>
      <p className="mt-1 font-sans text-sm text-ink/65">
        Share this 4-digit code with staff so they can request access to your school.
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="block min-w-0 flex-1">
          <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
            Code
          </span>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            disabled={saving}
            value={code}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, '').slice(0, 4));
              setError(null);
              setSaved(false);
            }}
            placeholder="1234"
            className={`${inputClassName} tracking-[0.2em]`}
          />
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={() => {
              const next = randomFourDigitCode();
              setCode(next);
              void saveCode(next);
            }}
            className="rounded-full bg-skysoft/70 px-4 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-skysoft disabled:cursor-not-allowed disabled:opacity-50"
          >
            Generate
          </button>
          <button
            type="button"
            disabled={saving || !/^\d{4}$/.test(code) || code === (school.accessConfirmationCode ?? '')}
            onClick={() => void saveCode(code)}
            className="rounded-full bg-white px-4 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-ink/5 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {error ? (
        <p className="mt-3 rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink" role="alert">
          {error}
        </p>
      ) : null}
      {saved ? (
        <p className="mt-3 rounded-xl bg-grass/30 px-3 py-2 font-sans text-sm font-semibold text-ink" role="status">
          Confirmation code saved.
        </p>
      ) : null}
    </div>
  );
};

const StaffAccessRequestsPanel = ({
  school,
  onSchoolUpdated,
}: {
  school: SchoolPayload;
  onSchoolUpdated: (school: SchoolPayload) => void;
}) => {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [requests, setRequests] = useState<SchoolAccessPayload[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const res = await fetch('/api/school/access-requests', { credentials: 'include' });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? `Failed to load requests (${res.status})`);
      }
      const data = (await res.json()) as { requests: SchoolAccessPayload[] };
      setRequests(data.requests);
      setStatus('ready');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load requests');
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = useCallback(
    async (accessId: string, action: 'approve' | 'reject' | 'revoke') => {
      setActingId(accessId);
      setError(null);
      try {
        const res = await fetch(`/api/school/access-requests/${accessId}/${action}`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!res.ok) {
          const data = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(data?.error ?? `${action} failed (${res.status})`);
        }
        const data = (await res.json()) as { access: SchoolAccessPayload };
        setRequests((prev) =>
          prev.map((item) => (item.id === data.access.id ? data.access : item)),
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : `${action} failed`);
      } finally {
        setActingId(null);
      }
    },
    [],
  );

  const pending = requests.filter((item) => item.status === 'pending');
  const approved = requests.filter((item) => item.status === 'approved');

  return (
    <div className="space-y-6">
      <AccessConfirmationCodeManager school={school} onSchoolUpdated={onSchoolUpdated} />

      <div className="space-y-4">
        <div>
          <h3 className="font-display text-lg font-semibold text-ink">Access requests</h3>
          <p className="mt-1 font-sans text-sm text-ink/65">
            Approve staff who request access with your School ID and confirmation code. Approved
            staff can use check-in / check-out, but cannot manage students or billing.
          </p>
        </div>

      {status === 'loading' ? (
        <p className="font-sans text-sm text-ink/60">Loading access requests…</p>
      ) : null}

      {error ? (
        <p className="rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink" role="alert">
          {error}
        </p>
      ) : null}

      {status === 'ready' && pending.length === 0 && approved.length === 0 ? (
        <p className="font-sans text-sm text-ink/60">No staff access requests yet.</p>
      ) : null}

      {pending.length > 0 ? (
        <div className="space-y-3">
          <h4 className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
            Pending
          </h4>
          {pending.map((item) => (
            <div
              key={item.id}
              className="flex items-start justify-between gap-4 rounded-2xl border-2 border-ink/10 bg-paper/50 px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="font-sans text-sm font-semibold text-ink">
                  {formatAccessUserName(item.user)}
                  {item.user?.email ? (
                    <span className="font-normal text-ink/60"> ({item.user.email})</span>
                  ) : null}
                </p>
                {item.note ? (
                  <p className="mt-1 font-sans text-sm text-ink/75">{item.note}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 flex-wrap justify-end gap-2">
                <button
                  type="button"
                  disabled={actingId === item.id}
                  onClick={() => void decide(item.id, 'approve')}
                  className="rounded-full bg-grass/40 px-4 py-2 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-grass/55 disabled:opacity-50"
                >
                  Approve
                </button>
                <button
                  type="button"
                  disabled={actingId === item.id}
                  onClick={() => void decide(item.id, 'reject')}
                  className="rounded-full bg-white/80 px-4 py-2 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-white disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {approved.length > 0 ? (
        <div className="space-y-3">
          <h4 className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
            Approved staff
          </h4>
          {approved.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between gap-4 rounded-2xl border-2 border-ink/10 bg-white/70 px-4 py-3"
            >
              <p className="min-w-0 flex-1 font-sans text-sm font-semibold text-ink">
                {formatAccessUserName(item.user)}
                {item.user?.email ? (
                  <span className="font-normal text-ink/60"> ({item.user.email})</span>
                ) : null}
              </p>
              <button
                type="button"
                disabled={actingId === item.id}
                onClick={() => void decide(item.id, 'revoke')}
                className="shrink-0 rounded-full bg-doghouse/20 px-4 py-2 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-doghouse/30 disabled:opacity-50"
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
      ) : null}
      </div>
    </div>
  );
};

const StaffSchoolProfileView = ({
  school,
  onLeft,
}: {
  school: SchoolPayload;
  onLeft: () => void;
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);

  const handleLeave = useCallback(async () => {
    setLeaving(true);
    setLeaveError(null);
    try {
      const res = await fetch('/api/school/access-requests/leave', {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? `Leave failed (${res.status})`);
      }
      setConfirmOpen(false);
      onLeft();
    } catch (e) {
      setLeaveError(e instanceof Error ? e.message : 'Leave failed');
    } finally {
      setLeaving(false);
    }
  }, [onLeft]);

  return (
    <>
      <section
        className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]"
        aria-labelledby="school-profile-section-heading"
      >
        <h2
          id="school-profile-section-heading"
          className="font-display text-xl font-semibold text-ink"
        >
          School Profile
        </h2>
        <p className="mt-1 font-sans text-sm text-ink/65">
          You have staff access to this school. Profile details are managed by the school owner.
        </p>
        <dl className="mt-6 space-y-4">
          <div>
            <dt className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
              School ID
            </dt>
            <dd className="mt-1 font-sans text-base font-semibold text-ink">{school.schoolId}</dd>
          </div>
          <div>
            <dt className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
              School name
            </dt>
            <dd className="mt-1 font-sans text-base font-semibold text-ink">{school.name}</dd>
          </div>
          <div>
            <dt className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
              Phone number
            </dt>
            <dd className="mt-1 font-sans text-base font-semibold text-ink">
              {school.phoneNumber}
            </dd>
          </div>
          <div>
            <dt className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
              Address
            </dt>
            <dd className="mt-1 font-sans text-base font-semibold text-ink">{school.address}</dd>
          </div>
          <div>
            <dt className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
              Time zone
            </dt>
            <dd className="mt-1 font-sans text-base font-semibold text-ink">{school.timezone}</dd>
          </div>
        </dl>

        <div className="mt-8 border-t border-ink/10 pt-6">
          <h3 className="font-display text-lg font-semibold text-ink">Leave school</h3>
          <p className="mt-1 font-sans text-sm text-ink/65">
            Exit this school to stop check-in access. You can request access again later with the
            School ID and confirmation code.
          </p>
          <button
            type="button"
            onClick={() => {
              setLeaveError(null);
              setConfirmOpen(true);
            }}
            className="mt-4 rounded-full bg-doghouse/20 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-doghouse/30"
          >
            Leave school
          </button>
        </div>
      </section>

      <Transition appear show={confirmOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[60]"
          onClose={() => {
            if (!leaving) setConfirmOpen(false);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-ink/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md rounded-3xl border-[3px] border-ink/15 bg-white p-6 shadow-[4px_8px_0_rgba(44,44,44,0.1)]">
                  <Dialog.Title className="font-display text-xl font-semibold text-ink">
                    Leave school?
                  </Dialog.Title>
                  <Dialog.Description className="mt-2 font-sans text-sm text-ink/70">
                    You will lose staff access to{' '}
                    <span className="font-semibold text-ink">{school.name}</span> (
                    {school.schoolId}). Check-in / check-out will no longer be available until you
                    are approved again.
                  </Dialog.Description>

                  {leaveError ? (
                    <p
                      className="mt-4 rounded-xl bg-doghouse/15 px-3 py-2 text-sm font-semibold text-ink"
                      role="alert"
                    >
                      {leaveError}
                    </p>
                  ) : null}

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setConfirmOpen(false)}
                      disabled={leaving}
                      className="rounded-full px-4 py-2 text-sm font-semibold text-ink/70 hover:bg-ink/5 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleLeave()}
                      disabled={leaving}
                      className="rounded-full bg-doghouse/80 px-5 py-2 text-sm font-semibold text-white ring-2 ring-ink/10 hover:bg-doghouse disabled:opacity-50"
                    >
                      {leaving ? 'Leaving…' : 'Leave school'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

const AccountSchoolProfileSection = () => {
  const { refreshUser } = useUser();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [school, setSchool] = useState<SchoolPayload | null>(null);
  const [role, setRole] = useState<SchoolRole>('none');
  const [myAccess, setMyAccess] = useState<SchoolAccessPayload | null>(null);
  const [studentLimit, setStudentLimit] = useState<StudentLimitStatus | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [profileTab, setProfileTab] = useState<'details' | 'staff'>('details');
  const [schoolId, setSchoolId] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [timezone, setTimezone] = useState(getBrowserTimeZone);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [schoolIdAvailability, setSchoolIdAvailability] = useState<
    'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  >('idle');
  const schoolIdCheckSeq = useRef(0);

  const hydrateForm = useCallback((next: SchoolPayload | null) => {
    setSchoolId(next?.schoolId ?? '');
    setName(next?.name ?? '');
    setPhoneNumber(next?.phoneNumber ?? '');
    setAddress(next?.address ?? '');
    setTimezone(next?.timezone || getBrowserTimeZone());
    setFieldErrors({});
    setSaved(false);
  }, []);

  const loadSchool = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const res = await fetch('/api/school', { credentials: 'include' });
      if (!res.ok) {
        throw new Error(`Failed to load school (${res.status})`);
      }
      const data = (await res.json()) as {
        school: SchoolPayload | null;
        role?: SchoolRole;
        myAccess?: SchoolAccessPayload | null;
        studentLimit?: StudentLimitStatus | null;
      };
      const nextRole = data.role ?? 'none';
      setSchool(data.school);
      setRole(nextRole);
      setMyAccess(data.myAccess ?? null);
      setStudentLimit(data.studentLimit ?? null);
      if (nextRole === 'owner' && data.school) {
        hydrateForm(data.school);
        setShowForm(true);
        setShowJoinForm(false);
      } else {
        hydrateForm(null);
        setShowForm(false);
        setShowJoinForm(false);
      }
      setStatus('ready');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load school');
      setStatus('error');
    }
  }, [hydrateForm]);

  useEffect(() => {
    void loadSchool();
  }, [loadSchool]);

  useEffect(() => {
    const trimmed = schoolId.trim();
    const formatError = validateSchoolId(schoolId);

    if (!trimmed) {
      setSchoolIdAvailability('idle');
      return;
    }

    // Unchanged from saved school — treat as available without a network call.
    if (school && trimmed === school.schoolId) {
      setSchoolIdAvailability('available');
      return;
    }

    if (formatError) {
      setSchoolIdAvailability('invalid');
      return;
    }

    const seq = ++schoolIdCheckSeq.current;
    setSchoolIdAvailability('checking');

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch(
            `/api/school/id-available?schoolId=${encodeURIComponent(trimmed)}`,
            { credentials: 'include' },
          );
          const data = (await res.json().catch(() => null)) as
            | { available?: boolean; error?: string }
            | null;

          if (seq !== schoolIdCheckSeq.current) return;

          if (!res.ok) {
            // Format / validation errors from the API
            if (res.status === 400) {
              setSchoolIdAvailability('invalid');
              return;
            }
            setSchoolIdAvailability('idle');
            return;
          }

          if (data?.available) {
            setSchoolIdAvailability('available');
          } else {
            setSchoolIdAvailability('taken');
          }
        } catch {
          if (seq === schoolIdCheckSeq.current) {
            setSchoolIdAvailability('idle');
          }
        }
      })();
    }, 300);

    return () => {
      window.clearTimeout(timer);
    };
  }, [school, schoolId]);

  const schoolIdFormatError = useMemo(() => validateSchoolId(schoolId), [schoolId]);

  const schoolIdLiveError = useMemo(() => {
    // Don't nag "required" on an empty field until submit; still block submit below.
    if (schoolIdFormatError === 'School ID is required.') {
      return undefined;
    }
    if (schoolIdFormatError) return schoolIdFormatError;
    if (schoolIdAvailability === 'taken') {
      return 'This School ID is already taken.';
    }
    return undefined;
  }, [schoolIdAvailability, schoolIdFormatError]);

  const displayedSchoolIdError = schoolIdLiveError ?? fieldErrors.schoolId;

  const hasSchoolIdError =
    Boolean(schoolIdFormatError) ||
    schoolIdAvailability === 'taken' ||
    schoolIdAvailability === 'invalid' ||
    schoolIdAvailability === 'checking';

  const hasChanges =
    schoolId !== (school?.schoolId ?? '') ||
    name !== (school?.name ?? '') ||
    phoneNumber !== (school?.phoneNumber ?? '') ||
    address !== (school?.address ?? '') ||
    timezone !== (school?.timezone ?? '');

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const errors = validateSchoolForm({ schoolId, name, phoneNumber, address });
      if (schoolIdAvailability === 'taken') {
        errors.schoolId = 'This School ID is already taken.';
      }
      setFieldErrors(errors);
      if (
        Object.keys(errors).length > 0 ||
        schoolIdAvailability === 'checking' ||
        schoolIdAvailability === 'invalid' ||
        schoolIdAvailability === 'taken'
      ) {
        return;
      }

      setSaving(true);
      setError(null);
      setSaved(false);

      try {
        const method = school ? 'PUT' : 'POST';
        const res = await fetch('/api/school', {
          method,
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            schoolId: schoolId.trim(),
            name: name.trim(),
            phoneNumber: phoneNumber.trim(),
            address: address.trim(),
            timezone,
          }),
        });

        if (!res.ok) {
          const data = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(data?.error ?? `Save failed (${res.status})`);
        }

        const data = (await res.json()) as {
          school: SchoolPayload;
          studentLimit?: StudentLimitStatus | null;
        };
        setSchool(data.school);
        setStudentLimit(data.studentLimit ?? null);
        setRole('owner');
        setMyAccess(null);
        hydrateForm(data.school);
        setShowForm(true);
        setSaved(true);
        await refreshUser();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Save failed');
      } finally {
        setSaving(false);
      }
    },
    [
      address,
      hydrateForm,
      name,
      phoneNumber,
      refreshUser,
      school,
      schoolId,
      schoolIdAvailability,
      timezone,
    ],
  );

  const handleAccessRequested = useCallback(
    async (access: SchoolAccessPayload) => {
      setMyAccess(access);
      setShowJoinForm(false);
      await refreshUser();
    },
    [refreshUser],
  );

  if (status === 'loading') {
    return (
      <section className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]">
        <p className="font-sans text-sm text-ink/60">Loading school profile…</p>
      </section>
    );
  }

  if (status === 'error' && !school && !showForm) {
    return (
      <section className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]">
        <h2 className="font-display text-xl font-semibold text-ink">School Profile</h2>
        <p className="mt-3 rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink" role="alert">
          {error}
        </p>
        <button
          type="button"
          onClick={() => void loadSchool()}
          className="mt-4 rounded-full bg-skysoft/70 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-skysoft"
        >
          Try again
        </button>
      </section>
    );
  }

  if (role === 'staff' && school) {
    return (
      <StaffSchoolProfileView
        school={school}
        onLeft={() => {
          void (async () => {
            await refreshUser();
            await loadSchool();
          })();
        }}
      />
    );
  }

  if (!showForm) {
    const pending = myAccess?.status === 'pending';
    const rejected = myAccess?.status === 'rejected' || myAccess?.status === 'revoked';

    return (
      <section
        className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]"
        aria-labelledby="school-profile-section-heading"
      >
        <h2
          id="school-profile-section-heading"
          className="font-display text-xl font-semibold text-ink"
        >
          School Profile
        </h2>

        {pending && myAccess ? (
          <div className="mt-4 rounded-2xl bg-skysoft/30 px-4 py-3 font-sans text-sm text-ink">
            <p className="font-semibold">Access request pending</p>
            <p className="mt-1 text-ink/80">
              Waiting for approval to join{' '}
              <span className="font-semibold">{myAccess.schoolName}</span> ({myAccess.schoolId}).
            </p>
            {myAccess.note ? (
              <p className="mt-2 text-ink/70">Your note: {myAccess.note}</p>
            ) : null}
          </div>
        ) : null}

        {rejected && myAccess && !showJoinForm ? (
          <div className="mt-4 rounded-2xl bg-doghouse/15 px-4 py-3 font-sans text-sm text-ink">
            <p className="font-semibold">
              {myAccess.status === 'revoked' ? 'Access revoked' : 'Request rejected'}
            </p>
            <p className="mt-1 text-ink/80">
              Your request for <span className="font-semibold">{myAccess.schoolName}</span> (
              {myAccess.schoolId}) was {myAccess.status}. You can request access again or create your
              own school.
            </p>
          </div>
        ) : null}

        {!pending && !showJoinForm ? (
          <>
            <p className="mt-1 font-sans text-sm text-ink/65">
              Create a school you own, or request staff access to an existing school with its School
              ID.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  hydrateForm(null);
                  setShowForm(true);
                  setShowJoinForm(false);
                }}
                className="rounded-full bg-skysoft/70 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-skysoft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/35"
              >
                Create school profile
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowJoinForm(true);
                  setShowForm(false);
                }}
                className="rounded-full bg-white/80 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/35"
              >
                Request staff access
              </button>
            </div>
          </>
        ) : null}

        {showJoinForm ? (
          <div className="mt-4">
            <p className="font-sans text-sm text-ink/65">
              Enter the school&apos;s public School ID. The owner will approve or reject your
              request.
            </p>
            <JoinSchoolForm onSubmitted={(access) => void handleAccessRequested(access)} />
            <button
              type="button"
              onClick={() => setShowJoinForm(false)}
              className="mt-3 rounded-full px-4 py-2.5 font-sans text-sm font-semibold text-ink/70 transition hover:bg-ink/5"
            >
              Cancel
            </button>
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <section
      className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]"
      aria-labelledby="school-profile-section-heading"
    >
      <h2
        id="school-profile-section-heading"
        className="font-display text-xl font-semibold text-ink"
      >
        School Profile
      </h2>
      <p className="mt-1 font-sans text-sm text-ink/65">
        {school
          ? profileTab === 'staff'
            ? 'Manage who can check students in and out for your school.'
            : 'Update your school details, grades, rooms, and teachers.'
          : 'Enter your school details to create a profile.'}
      </p>

      {studentLimit?.overLimit && studentLimit.limit !== null ? (
        <div
          className="mt-4 rounded-2xl bg-doghouse/20 px-4 py-3 font-sans text-sm text-ink"
          role="alert"
        >
          <p className="font-semibold">You&apos;re over your student quota</p>
          <p className="mt-1 text-ink/80">
            This school has {studentLimit.studentCount} students, but{' '}
            {studentLimit.planName ? (
              <>
                <span className="font-semibold">{studentLimit.planName}</span> allows
              </>
            ) : (
              'your current plan allows'
            )}{' '}
            {studentLimit.limit}. Check-in and check-out are disabled until you
            upgrade or remove students.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link
              to={ACCOUNT_BILLING_PATH}
              className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-white hover:ring-ink/20"
            >
              Upgrade billing
            </Link>
            {school?.schoolId ? (
              <Link
                to={`/attendance/${encodeURIComponent(school.schoolId)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-white hover:ring-ink/20"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0 text-ink/70" aria-hidden />
                Open attendance page
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}

      {school ? (
        <div
          className="mt-5 flex w-fit gap-1 rounded-full bg-ink/5 p-1"
          role="tablist"
          aria-label="School profile sections"
        >
          <button
            type="button"
            role="tab"
            aria-selected={profileTab === 'details'}
            onClick={() => setProfileTab('details')}
            className={`rounded-full px-4 py-2 font-sans text-sm font-semibold transition ${
              profileTab === 'details'
                ? 'bg-white text-ink shadow-sm ring-1 ring-ink/10'
                : 'text-ink/60 hover:text-ink'
            }`}
          >
            School details
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={profileTab === 'staff'}
            onClick={() => setProfileTab('staff')}
            className={`rounded-full px-4 py-2 font-sans text-sm font-semibold transition ${
              profileTab === 'staff'
                ? 'bg-white text-ink shadow-sm ring-1 ring-ink/10'
                : 'text-ink/60 hover:text-ink'
            }`}
          >
            Staff access
          </button>
        </div>
      ) : null}

      {school && profileTab === 'staff' ? (
        <div className="mt-6">
          <StaffAccessRequestsPanel school={school} onSchoolUpdated={setSchool} />
        </div>
      ) : (
        <>
          <form className="mt-6 space-y-5" onSubmit={(event) => void handleSubmit(event)} noValidate>
            <label className="block">
              <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                School ID
              </span>
              <input
                type="text"
                name="schoolId"
                value={schoolId}
                onChange={(event) => {
                  setSchoolId(event.target.value);
                  setSaved(false);
                  setFieldErrors((prev) => ({ ...prev, schoolId: undefined }));
                }}
                autoComplete="off"
                spellCheck={false}
                aria-invalid={Boolean(displayedSchoolIdError)}
                aria-describedby={
                  displayedSchoolIdError ? 'school-id-error' : 'school-id-hint'
                }
                className={displayedSchoolIdError ? inputErrorClassName : inputClassName}
              />
              {displayedSchoolIdError ? (
                <p
                  id="school-id-error"
                  className="mt-1 font-sans text-xs font-semibold text-doghouse"
                  role="alert"
                >
                  {displayedSchoolIdError}
                </p>
              ) : schoolIdAvailability === 'checking' ? (
                <p id="school-id-hint" className="mt-1 font-sans text-xs text-ink/55">
                  Checking availability…
                </p>
              ) : schoolIdAvailability === 'available' && schoolId.trim() ? (
                <p id="school-id-hint" className="mt-1 font-sans text-xs font-semibold text-ink/70">
                  School ID is available.
                </p>
              ) : (
                <p id="school-id-hint" className="mt-1 font-sans text-xs text-ink/55">
                  {SCHOOL_ID_FORMAT_HINT}
                </p>
              )}
            </label>

            <label className="block">
              <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                School name
              </span>
              <input
                type="text"
                name="name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setSaved(false);
                  setFieldErrors((prev) => ({ ...prev, name: undefined }));
                }}
                autoComplete="organization"
                aria-invalid={Boolean(fieldErrors.name)}
                aria-describedby={fieldErrors.name ? 'school-name-error' : undefined}
                className={fieldErrors.name ? inputErrorClassName : inputClassName}
              />
              {fieldErrors.name ? (
                <p id="school-name-error" className="mt-1 font-sans text-xs font-semibold text-doghouse" role="alert">
                  {fieldErrors.name}
                </p>
              ) : null}
            </label>

            <label className="block">
              <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                Phone number
              </span>
              <input
                type="tel"
                name="phoneNumber"
                value={phoneNumber}
                onChange={(event) => {
                  setPhoneNumber(event.target.value);
                  setSaved(false);
                  setFieldErrors((prev) => ({ ...prev, phoneNumber: undefined }));
                }}
                autoComplete="tel"
                aria-invalid={Boolean(fieldErrors.phoneNumber)}
                aria-describedby={fieldErrors.phoneNumber ? 'school-phone-error' : undefined}
                className={fieldErrors.phoneNumber ? inputErrorClassName : inputClassName}
              />
              {fieldErrors.phoneNumber ? (
                <p id="school-phone-error" className="mt-1 font-sans text-xs font-semibold text-doghouse" role="alert">
                  {fieldErrors.phoneNumber}
                </p>
              ) : null}
            </label>

            <label className="block">
              <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                Address
              </span>
              <textarea
                name="address"
                value={address}
                rows={3}
                onChange={(event) => {
                  setAddress(event.target.value);
                  setSaved(false);
                  setFieldErrors((prev) => ({ ...prev, address: undefined }));
                }}
                autoComplete="street-address"
                aria-invalid={Boolean(fieldErrors.address)}
                aria-describedby={fieldErrors.address ? 'school-address-error' : undefined}
                className={fieldErrors.address ? inputErrorClassName : inputClassName}
              />
              {fieldErrors.address ? (
                <p id="school-address-error" className="mt-1 font-sans text-xs font-semibold text-doghouse" role="alert">
                  {fieldErrors.address}
                </p>
              ) : null}
            </label>

            <label className="block">
              <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                Time zone
              </span>
              <select
                name="timezone"
                value={timezone}
                onChange={(event) => {
                  setTimezone(event.target.value);
                  setSaved(false);
                }}
                className={inputClassName}
              >
                {getTimeZoneOptions(timezone).map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
              <p className="mt-1 font-sans text-xs text-ink/55">
                Used to decide which calendar day check-ins and check-outs belong to. Defaults to this
                device&apos;s time zone.
              </p>
            </label>

            {error ? (
              <p className="rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink" role="alert">
                {error}
              </p>
            ) : null}

            {saved ? (
              <p className="rounded-xl bg-grass/30 px-3 py-2 font-sans text-sm font-semibold text-ink" role="status">
                School profile saved.
              </p>
            ) : null}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={
                  saving ||
                  hasSchoolIdError ||
                  (Boolean(school) && !hasChanges)
                }
                className="rounded-full bg-skysoft/70 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-skysoft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/35 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? 'Saving…' : school ? 'Save school profile' : 'Create school profile'}
              </button>
              {!school ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    setShowForm(false);
                    setError(null);
                    setFieldErrors({});
                  }}
                  className="rounded-full px-4 py-2.5 font-sans text-sm font-semibold text-ink/70 transition hover:bg-ink/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
                >
                  Cancel
                </button>
              ) : null}
            </div>
          </form>

          {school ? (
            <div className="mt-8 space-y-4 border-t border-ink/10 pt-8">
              <div>
                <h3 className="font-display text-lg font-semibold text-ink">
                  Grades, rooms, and teachers
                </h3>
                <p className="mt-1 font-sans text-sm text-ink/65">
                  These options are used when adding students.
                </p>
              </div>
              <OptionTableEditor
                kind="grades"
                items={school.grades ?? []}
                onSchoolUpdated={setSchool}
              />
              <OptionTableEditor
                kind="rooms"
                items={school.rooms ?? []}
                onSchoolUpdated={setSchool}
              />
              <OptionTableEditor
                kind="teachers"
                items={school.teachers ?? []}
                onSchoolUpdated={setSchool}
              />
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};

export default AccountSchoolProfileSection;
